import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { CalendarIcon, FileText, Loader2, Pencil, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import {
	DocumentsPreviewButton,
	type PreviewDocumentLink,
} from "@/components/internal/shared/DocumentsPreviewButton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { cn } from "@/lib/utils";

type InboundDeliveryStatus = "scheduled" | "received";
type InboundDeliveryDocumentType = "do" | "bol" | "driverId";
type InboundDeliveriesTab = "all" | InboundDeliveryStatus;

type InboundDeliveryDocumentMetadata = {
	url: string;
	r2Key: string;
	filename: string;
	originalFilename?: string;
	contentType: string;
	fileSize: number;
	uploadedAt: number;
};

type InboundDeliveryRecord = {
	_id: string;
	id: string;
	shipperName: string;
	scheduledDeliveryAt: number;
	trackingReference?: string;
	driverName?: string;
	timeIn?: number;
	timeOut?: number;
	warehouseReportComplete: boolean;
	damage: boolean;
	notes?: string;
	doDocument?: InboundDeliveryDocumentMetadata;
	bolDocument?: InboundDeliveryDocumentMetadata;
	driverIdDocument?: InboundDeliveryDocumentMetadata;
	status: InboundDeliveryStatus;
	receivedAt?: number;
	createdAt: number;
	updatedAt: number;
	createdByLabel?: string;
	updatedByLabel?: string;
};

type InboundDeliveryFormState = {
	shipperName: string;
	scheduledDeliveryAt: string;
	trackingReference: string;
	driverName: string;
	timeIn: string;
	timeOut: string;
	warehouseReportComplete: boolean;
	damage: boolean;
	notes: string;
	status: InboundDeliveryStatus;
};

type InboundDeliveryDocumentDraft = {
	file: File | null;
	removeExisting: boolean;
};

type InboundDeliveryDocumentDrafts = Record<
	InboundDeliveryDocumentType,
	InboundDeliveryDocumentDraft
>;

type InboundDeliveryDialogState =
	| { mode: "create" }
	| { mode: "edit"; delivery: InboundDeliveryRecord };

const LOGISTICS_TIME_ZONE = "America/Chicago";
const INBOUND_DELIVERY_DOCUMENT_MAX_BYTES = 1024 * 1024;
const INBOUND_DOCUMENT_LABELS: Record<InboundDeliveryDocumentType, string> = {
	do: "Delivery Order",
	bol: "Bill of Lading",
	driverId: "Driver ID",
};

const LOGISTICS_DATE_TIME_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
	timeZone: LOGISTICS_TIME_ZONE,
	hourCycle: "h23",
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
	hour: "2-digit",
	minute: "2-digit",
	second: "2-digit",
});

const LOGISTICS_DATE_TIME_DISPLAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
	timeZone: LOGISTICS_TIME_ZONE,
	month: "short",
	day: "numeric",
	year: "numeric",
	hour: "numeric",
	minute: "2-digit",
	timeZoneName: "short",
});

const LOGISTICS_TIME_DISPLAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
	timeZone: LOGISTICS_TIME_ZONE,
	hour: "numeric",
	minute: "2-digit",
});

const INPUT_LABEL_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
	timeZone: "UTC",
	month: "short",
	day: "numeric",
	year: "numeric",
});

const TIME_INPUT_PATTERN = /^(\d{2}):(\d{2})$/;

function getStatusLabel(status: InboundDeliveryStatus): string {
	return status === "received" ? "Received" : "Scheduled";
}

function getStatusBadgeClass(status: InboundDeliveryStatus): string {
	if (status === "received") {
		return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	}
	return "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200";
}

function getBooleanBadgeClass(value: boolean, tone: "default" | "danger" = "default"): string {
	if (value) {
		if (tone === "danger") {
			return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
		}
		return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
	}
	return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
}

function createEmptyDocumentDrafts(): InboundDeliveryDocumentDrafts {
	return {
		do: { file: null, removeExisting: false },
		bol: { file: null, removeExisting: false },
		driverId: { file: null, removeExisting: false },
	};
}

function getDeliveryDocument(
	delivery: InboundDeliveryRecord | undefined,
	documentType: InboundDeliveryDocumentType,
) {
	if (!delivery) return undefined;
	if (documentType === "do") return delivery.doDocument;
	if (documentType === "bol") return delivery.bolDocument;
	return delivery.driverIdDocument;
}

function buildInboundDocumentLinks(delivery: InboundDeliveryRecord): PreviewDocumentLink[] {
	const links: PreviewDocumentLink[] = [];
	if (delivery.doDocument?.url) {
		links.push({
			key: `${delivery._id}-do`,
			label: INBOUND_DOCUMENT_LABELS.do,
			url: delivery.doDocument.url,
			sourceLabel: "PDF",
		});
	}
	if (delivery.bolDocument?.url) {
		links.push({
			key: `${delivery._id}-bol`,
			label: INBOUND_DOCUMENT_LABELS.bol,
			url: delivery.bolDocument.url,
			sourceLabel: "PDF",
		});
	}
	if (delivery.driverIdDocument?.url) {
		links.push({
			key: `${delivery._id}-driver-id`,
			label: INBOUND_DOCUMENT_LABELS.driverId,
			url: delivery.driverIdDocument.url,
			sourceLabel: "PDF",
		});
	}
	return links;
}

function isPdfFile(file: File): boolean {
	return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function formatFileSize(fileSize: number | undefined): string {
	if (typeof fileSize !== "number" || !Number.isFinite(fileSize) || fileSize <= 0) {
		return "";
	}
	if (fileSize >= 1024 * 1024) {
		return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
	}
	return `${Math.max(1, Math.round(fileSize / 1024))} KB`;
}

function formatChicagoDateTime(timestamp: number | undefined): string {
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "-";
	return LOGISTICS_DATE_TIME_DISPLAY_FORMATTER.format(new Date(timestamp));
}

function formatChicagoTime(timestamp: number | undefined): string {
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "-";
	return LOGISTICS_TIME_DISPLAY_FORMATTER.format(new Date(timestamp));
}

function splitDateTimeValue(value: string | undefined) {
	const normalized = String(value ?? "").trim();
	if (!normalized) {
		return { datePart: "", timePart: "" };
	}

	const [rawDatePart = "", rawTimePart = ""] = normalized.split("T");
	const timePart = rawTimePart.slice(0, 5);
	return {
		datePart: /^\d{4}-\d{2}-\d{2}$/.test(rawDatePart) ? rawDatePart : "",
		timePart: TIME_INPUT_PATTERN.test(timePart) ? timePart : "",
	};
}

function buildDateTimeValue(datePart: string, timePart: string): string {
	if (!datePart) return "";
	return timePart ? `${datePart}T${timePart}` : `${datePart}T`;
}

function toDateInputValue(date: Date | undefined): string {
	if (!date || Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function formatChicagoInputLabel(value: string | undefined, placeholder: string): string {
	const parts = splitDateTimeValue(value);
	if (!parts.datePart) return placeholder;

	const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = parts.datePart
		.split("-")
		.map(Number);
	const displayDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
	if (Number.isNaN(displayDate.getTime())) return placeholder;

	return INPUT_LABEL_DATE_FORMATTER.format(displayDate);
}

function parseTimeInputValue(value: string): {
	value: string | undefined;
	invalid: boolean;
} {
	const normalized = value.trim();
	if (!normalized) return { value: undefined, invalid: false };

	const match = normalized.match(TIME_INPUT_PATTERN);
	if (!match) return { value: undefined, invalid: true };

	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (
		!Number.isInteger(hour) ||
		!Number.isInteger(minute) ||
		hour < 0 ||
		hour > 23 ||
		minute < 0 ||
		minute > 59
	) {
		return { value: undefined, invalid: true };
	}

	return {
		value: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
		invalid: false,
	};
}

function combineDatePartAndTimeValue(
	dateTimeValue: string,
	timeValue: string | undefined,
): ParsedDateTimeInput {
	if (!timeValue) return { value: undefined, invalid: false };
	const { datePart } = splitDateTimeValue(dateTimeValue);
	if (!datePart) return { value: undefined, invalid: true };
	return parseDateTimeInputValue(`${datePart}T${timeValue}`);
}

function getCurrentChicagoDateTimeValue(): string {
	return formatTimestampForDateTimeInput(Date.now());
}

function getCurrentChicagoTimeValue(): string {
	return formatTimestampForTimeInput(Date.now());
}

function buildEmptyFormState(): InboundDeliveryFormState {
	return {
		shipperName: "",
		scheduledDeliveryAt: getCurrentChicagoDateTimeValue(),
		trackingReference: "",
		driverName: "",
		timeIn: "",
		timeOut: "",
		warehouseReportComplete: false,
		damage: false,
		notes: "",
		status: "scheduled",
	};
}

function buildFormState(delivery?: InboundDeliveryRecord): InboundDeliveryFormState {
	if (!delivery) return buildEmptyFormState();
	return {
		shipperName: delivery.shipperName,
		scheduledDeliveryAt: formatTimestampForDateTimeInput(delivery.scheduledDeliveryAt),
		trackingReference: delivery.trackingReference ?? "",
		driverName: delivery.driverName ?? "",
		timeIn: formatTimestampForTimeInput(delivery.timeIn),
		timeOut: formatTimestampForTimeInput(delivery.timeOut),
		warehouseReportComplete: delivery.warehouseReportComplete,
		damage: delivery.damage,
		notes: delivery.notes ?? "",
		status: delivery.status,
	};
}

type ParsedDateTimeInput = {
	value: number | undefined;
	invalid: boolean;
};

type LogisticsDateTimeParts = {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
};

function getLogisticsDateTimeParts(timestamp: number): LogisticsDateTimeParts | null {
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return null;

	const parts = LOGISTICS_DATE_TIME_PARTS_FORMATTER.formatToParts(date);
	const values = {
		year: Number(parts.find((part) => part.type === "year")?.value ?? Number.NaN),
		month: Number(parts.find((part) => part.type === "month")?.value ?? Number.NaN),
		day: Number(parts.find((part) => part.type === "day")?.value ?? Number.NaN),
		hour: Number(parts.find((part) => part.type === "hour")?.value ?? Number.NaN),
		minute: Number(parts.find((part) => part.type === "minute")?.value ?? Number.NaN),
		second: Number(parts.find((part) => part.type === "second")?.value ?? Number.NaN),
	};

	return Object.values(values).every((value) => Number.isFinite(value)) ? values : null;
}

function getLogisticsTimeZoneOffsetMs(timestamp: number): number | null {
	const parts = getLogisticsDateTimeParts(timestamp);
	if (!parts) return null;

	const zonedUtcTimestamp = Date.UTC(
		parts.year,
		parts.month - 1,
		parts.day,
		parts.hour,
		parts.minute,
		parts.second,
		0,
	);
	return zonedUtcTimestamp - timestamp;
}

function formatLogisticsDateTimeInputValue(parts: LogisticsDateTimeParts): string {
	const month = String(parts.month).padStart(2, "0");
	const day = String(parts.day).padStart(2, "0");
	const hour = String(parts.hour).padStart(2, "0");
	const minute = String(parts.minute).padStart(2, "0");

	return `${parts.year}-${month}-${day}T${hour}:${minute}`;
}

function parseLogisticsDateTimeInputParts(value: string): LogisticsDateTimeParts | null {
	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
	if (!match) return null;

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const hour = Number(match[4]);
	const minute = Number(match[5]);

	if (
		!Number.isInteger(year) ||
		!Number.isInteger(month) ||
		!Number.isInteger(day) ||
		!Number.isInteger(hour) ||
		!Number.isInteger(minute) ||
		month < 1 ||
		month > 12 ||
		day < 1 ||
		day > 31 ||
		hour < 0 ||
		hour > 23 ||
		minute < 0 ||
		minute > 59
	) {
		return null;
	}

	const validationDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
	if (
		validationDate.getUTCFullYear() !== year ||
		validationDate.getUTCMonth() !== month - 1 ||
		validationDate.getUTCDate() !== day ||
		validationDate.getUTCHours() !== hour ||
		validationDate.getUTCMinutes() !== minute
	) {
		return null;
	}

	return { year, month, day, hour, minute, second: 0 };
}

function formatTimestampForDateTimeInput(timestamp: unknown): string {
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "";
	const parts = getLogisticsDateTimeParts(timestamp);
	return parts ? formatLogisticsDateTimeInputValue(parts) : "";
}

function formatTimestampForTimeInput(timestamp: unknown): string {
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "";
	const parts = getLogisticsDateTimeParts(timestamp);
	if (!parts) return "";
	return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

function parseDateTimeInputValue(value: string): ParsedDateTimeInput {
	const normalized = value.trim();
	if (!normalized) return { value: undefined, invalid: false };

	const parts = parseLogisticsDateTimeInputParts(normalized);
	if (!parts) return { value: undefined, invalid: true };

	const utcGuess = Date.UTC(
		parts.year,
		parts.month - 1,
		parts.day,
		parts.hour,
		parts.minute,
		0,
		0,
	);

	const initialOffset = getLogisticsTimeZoneOffsetMs(utcGuess);
	if (initialOffset === null) return { value: undefined, invalid: true };

	let timestamp = utcGuess - initialOffset;
	const resolvedOffset = getLogisticsTimeZoneOffsetMs(timestamp);
	if (resolvedOffset === null) return { value: undefined, invalid: true };
	if (resolvedOffset !== initialOffset) {
		timestamp = utcGuess - resolvedOffset;
	}

	const resolvedParts = getLogisticsDateTimeParts(timestamp);
	if (
		!resolvedParts ||
		resolvedParts.year !== parts.year ||
		resolvedParts.month !== parts.month ||
		resolvedParts.day !== parts.day ||
		resolvedParts.hour !== parts.hour ||
		resolvedParts.minute !== parts.minute
	) {
		return { value: undefined, invalid: true };
	}

	return { value: timestamp, invalid: false };
}

function ChicagoDateTimePicker({
	id,
	value,
	onChange,
	placeholder,
	defaultTime = getCurrentChicagoTimeValue(),
}: {
	id: string;
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	defaultTime?: string;
}) {
	const [open, setOpen] = React.useState(false);
	const parts = React.useMemo(() => splitDateTimeValue(value), [value]);
	const selectedDate = React.useMemo(
		() => (parts.datePart ? new Date(`${parts.datePart}T00:00:00`) : undefined),
		[parts.datePart],
	);

	const handleDateSelect = React.useCallback(
		(nextDate: Date | undefined) => {
			if (!nextDate) {
				onChange("");
				setOpen(false);
				return;
			}

			const datePart = toDateInputValue(nextDate);
			onChange(buildDateTimeValue(datePart, parts.timePart || defaultTime));
			setOpen(false);
		},
		[defaultTime, onChange, parts.timePart],
	);

	return (
		<div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_148px]">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						id={id}
						type="button"
						variant="outline"
						className={cn(
							"w-full justify-start text-left font-normal",
							!parts.datePart && "text-muted-foreground",
						)}
					>
						<CalendarIcon className="mr-2 size-4" />
						{formatChicagoInputLabel(value, placeholder)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="single"
						selected={selectedDate}
						onSelect={handleDateSelect}
						defaultMonth={selectedDate ?? new Date()}
						initialFocus
					/>
				</PopoverContent>
			</Popover>
			<div>
				<Input
					type="time"
					value={parts.timePart}
					onChange={(event) => onChange(buildDateTimeValue(parts.datePart, event.target.value))}
					disabled={!parts.datePart}
				/>
			</div>
		</div>
	);
}

function ChicagoTimeInput({
	id,
	value,
	onChange,
	disabled = false,
}: {
	id: string;
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
}) {
	return (
		<div>
			<Input
				id={id}
				type="time"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				disabled={disabled}
			/>
		</div>
	);
}

function InboundDeliveryDocumentsCell({ delivery }: { delivery: InboundDeliveryRecord }) {
	const documents = React.useMemo(() => buildInboundDocumentLinks(delivery), [delivery]);

	return (
		<DocumentsPreviewButton
			documents={documents}
			title={`Inbound Documents · ${delivery.shipperName}`}
			description="Stored DO, BOL, and Driver ID PDFs for this inbound delivery."
		/>
	);
}

function InboundDocumentUploadField({
	documentType,
	existingDocument,
	draft,
	onSelectFile,
	onClearSelectedFile,
	onMarkRemoveExisting,
	onUndoRemoveExisting,
}: {
	documentType: InboundDeliveryDocumentType;
	existingDocument?: InboundDeliveryDocumentMetadata;
	draft: InboundDeliveryDocumentDraft;
	onSelectFile: (file: File) => void;
	onClearSelectedFile: () => void;
	onMarkRemoveExisting: () => void;
	onUndoRemoveExisting: () => void;
}) {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const label = INBOUND_DOCUMENT_LABELS[documentType];
	const selectedFile = draft.file;
	const isRemovingExisting = draft.removeExisting;
	const currentFilename = selectedFile?.name ?? existingDocument?.originalFilename ?? existingDocument?.filename;
	const fileSizeLabel = selectedFile
		? formatFileSize(selectedFile.size)
		: formatFileSize(existingDocument?.fileSize);
	const statusLabel = selectedFile
		? existingDocument && !isRemovingExisting
			? "Will replace current PDF when you save."
			: "Will upload when you save."
		: isRemovingExisting
			? "Will be removed when you save."
			: existingDocument
				? "Already uploaded to this inbound delivery."
				: "Optional PDF. Leave blank if you do not have it yet.";

	return (
		<div className="rounded-lg border bg-background p-4">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="min-w-0 space-y-1">
					<div className="flex items-center gap-2">
						<FileText className="size-4 text-muted-foreground" />
						<p className="text-sm font-medium text-foreground">{label}</p>
					</div>
					<p className="text-xs text-muted-foreground">{statusLabel}</p>
					{currentFilename ? (
						<p className="truncate text-sm text-foreground">
							{currentFilename}
							{fileSizeLabel ? <span className="text-muted-foreground"> · {fileSizeLabel}</span> : null}
						</p>
					) : null}
				</div>

				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						variant={existingDocument && !selectedFile && !isRemovingExisting ? "outline" : "default"}
						size="sm"
						onClick={() => inputRef.current?.click()}
					>
						<Upload className="mr-2 size-4" />
						{existingDocument && !isRemovingExisting ? "Replace PDF" : "Choose PDF"}
					</Button>

					{selectedFile ? (
						<Button type="button" variant="ghost" size="sm" onClick={onClearSelectedFile}>
							<X className="mr-2 size-4" />
							Clear
						</Button>
					) : null}

					{existingDocument && !selectedFile && !isRemovingExisting ? (
						<Button type="button" variant="ghost" size="sm" onClick={onMarkRemoveExisting}>
							<X className="mr-2 size-4" />
							Remove
						</Button>
					) : null}

					{isRemovingExisting ? (
						<Button type="button" variant="ghost" size="sm" onClick={onUndoRemoveExisting}>
							Undo Remove
						</Button>
					) : null}
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="application/pdf,.pdf"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];
					if (file) onSelectFile(file);
					event.currentTarget.value = "";
				}}
			/>
		</div>
	);
}

function useColumns(onEdit: (delivery: InboundDeliveryRecord) => void) {
	return React.useMemo<ColumnDef<InboundDeliveryRecord>[]>(
		() => [
			{
				id: "status",
				accessorKey: "status",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
				cell: ({ row }) => (
					<Badge variant="outline" className={getStatusBadgeClass(row.original.status)}>
						{getStatusLabel(row.original.status)}
					</Badge>
				),
				meta: {
					label: "Status",
					variant: "select",
					options: [
						{ label: "Scheduled", value: "scheduled" },
						{ label: "Received", value: "received" },
					],
				},
				enableColumnFilter: true,
			},
			{
				id: "shipperName",
				accessorKey: "shipperName",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Shipper Name" />,
				meta: { label: "Shipper Name", placeholder: "Search shippers", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "scheduledDeliveryAt",
				accessorKey: "scheduledDeliveryAt",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Scheduled Delivery" />,
				cell: ({ row }) => <span className="text-sm">{formatChicagoDateTime(row.original.scheduledDeliveryAt)}</span>,
				meta: { label: "Scheduled Delivery", variant: "text", placeholder: "Search schedule" },
				enableColumnFilter: false,
			},
			{
				id: "trackingReference",
				accessorFn: (row) => row.trackingReference ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Tracking / Ref #" />,
				cell: ({ row }) => <span className="text-sm">{row.original.trackingReference || "-"}</span>,
				meta: { label: "Tracking / Ref #", placeholder: "Search tracking or ref #", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "driverName",
				accessorFn: (row) => row.driverName ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Driver Name" />,
				cell: ({ row }) => <span className="text-sm">{row.original.driverName || "-"}</span>,
				meta: { label: "Driver Name", placeholder: "Search drivers", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "timeIn",
				accessorKey: "timeIn",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Time In" />,
				cell: ({ row }) => <span className="text-sm">{formatChicagoTime(row.original.timeIn)}</span>,
			},
			{
				id: "timeOut",
				accessorKey: "timeOut",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Time Out" />,
				cell: ({ row }) => <span className="text-sm">{formatChicagoTime(row.original.timeOut)}</span>,
			},
			{
				id: "warehouseReportComplete",
				accessorFn: (row) => (row.warehouseReportComplete ? "yes" : "no"),
				header: ({ column }) => <DataTableColumnHeader column={column} label="Warehouse Report" />,
				cell: ({ row }) => (
					<Badge variant="outline" className={getBooleanBadgeClass(row.original.warehouseReportComplete)}>
						{row.original.warehouseReportComplete ? "Yes" : "No"}
					</Badge>
				),
				meta: {
					label: "Warehouse Report",
					variant: "select",
					options: [
						{ label: "Yes", value: "yes" },
						{ label: "No", value: "no" },
					],
				},
				enableColumnFilter: true,
			},
			{
				id: "damage",
				accessorFn: (row) => (row.damage ? "yes" : "no"),
				header: ({ column }) => <DataTableColumnHeader column={column} label="Damage" />,
				cell: ({ row }) => (
					<Badge variant="outline" className={getBooleanBadgeClass(row.original.damage, "danger")}>
						{row.original.damage ? "Yes" : "No"}
					</Badge>
				),
				meta: {
					label: "Damage",
					variant: "select",
					options: [
						{ label: "Yes", value: "yes" },
						{ label: "No", value: "no" },
					],
				},
				enableColumnFilter: true,
			},
			{
				id: "documents",
				accessorFn: (row) => buildInboundDocumentLinks(row).length,
				header: ({ column }) => <DataTableColumnHeader column={column} label="Docs" />,
				cell: ({ row }) => <InboundDeliveryDocumentsCell delivery={row.original} />,
				meta: { label: "Docs", variant: "number", placeholder: "Min docs" },
				enableColumnFilter: true,
			},
			{
				id: "notes",
				accessorFn: (row) => row.notes ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} label="Notes" />,
				cell: ({ row }) => {
					const notes = row.original.notes?.trim();
					if (!notes) return <span className="text-muted-foreground">-</span>;
					return (
						<span className="block max-w-[18rem] truncate text-sm text-muted-foreground" title={notes}>
							{notes}
						</span>
					);
				},
				meta: { label: "Notes", placeholder: "Search notes", variant: "text" },
				enableColumnFilter: true,
			},
			{
				id: "actions",
				header: () => <span className="sr-only">Actions</span>,
				cell: ({ row }) => (
					<Button variant="ghost" size="sm" onClick={() => onEdit(row.original)}>
						<Pencil className="mr-1.5 size-3.5" />
						Edit
					</Button>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		],
		[onEdit],
	);
}

export default function LogisticsInboundDeliveriesPage() {
	const { authUserId } = useAuthIdentity();
	const inboundDeliveries = useQuery(
		api.inboundDeliveries.listInboundDeliveries,
		authUserId ? { authUserId } : "skip",
	);
	const createInboundDelivery = useMutation(api.inboundDeliveries.createInboundDelivery);
	const updateInboundDelivery = useMutation(api.inboundDeliveries.updateInboundDelivery);
	const uploadInboundDeliveryDocument = useAction(api.inboundDeliveries.uploadInboundDeliveryDocument);
	const removeInboundDeliveryDocument = useAction(api.inboundDeliveries.removeInboundDeliveryDocument);

	const [activeTab, setActiveTab] = React.useState<InboundDeliveriesTab>("all");
	const [dialogState, setDialogState] = React.useState<InboundDeliveryDialogState | null>(null);
	const [form, setForm] = React.useState<InboundDeliveryFormState>(buildEmptyFormState());
	const [documentDrafts, setDocumentDrafts] = React.useState<InboundDeliveryDocumentDrafts>(
		createEmptyDocumentDrafts(),
	);
	const [isSaving, setIsSaving] = React.useState(false);

	React.useEffect(() => {
		if (!dialogState) return;
		setForm(
				dialogState.mode === "edit"
					? buildFormState(dialogState.delivery)
					: buildEmptyFormState(),
		);
		setDocumentDrafts(createEmptyDocumentDrafts());
	}, [dialogState]);

	const openCreateDialog = React.useCallback(() => {
		setDialogState({ mode: "create" });
	}, []);

	const openEditDialog = React.useCallback((delivery: InboundDeliveryRecord) => {
		setDialogState({ mode: "edit", delivery });
	}, []);

	const columns = useColumns(openEditDialog);

	const deliveries = React.useMemo(() => {
		return (inboundDeliveries ?? []) as InboundDeliveryRecord[];
	}, [inboundDeliveries]);

	const tabCounts = React.useMemo(
		() => ({
			all: deliveries.length,
			scheduled: deliveries.filter((delivery) => delivery.status === "scheduled").length,
			received: deliveries.filter((delivery) => delivery.status === "received").length,
		}),
		[deliveries],
	);

	const filteredDeliveries = React.useMemo(() => {
		const nextDeliveries =
			activeTab === "all"
				? [...deliveries]
				: deliveries.filter((delivery) => delivery.status === activeTab);

		nextDeliveries.sort((left, right) => {
			if (left.status !== right.status) {
				return left.status === "scheduled" ? -1 : 1;
			}

			if (left.status === "scheduled") {
				return left.scheduledDeliveryAt - right.scheduledDeliveryAt;
			}

			return (right.receivedAt ?? right.updatedAt) - (left.receivedAt ?? left.updatedAt);
		});

		return nextDeliveries;
	}, [activeTab, deliveries]);

	const setField = React.useCallback(
		<K extends keyof InboundDeliveryFormState>(field: K, value: InboundDeliveryFormState[K]) => {
			setForm((current) => ({ ...current, [field]: value }));
		},
		[],
	);

	const setDocumentDraft = React.useCallback(
		(
			documentType: InboundDeliveryDocumentType,
			updater: (current: InboundDeliveryDocumentDraft) => InboundDeliveryDocumentDraft,
		) => {
			setDocumentDrafts((current) => ({
				...current,
				[documentType]: updater(current[documentType]),
			}));
		},
		[],
	);

	const handleDocumentFileSelection = React.useCallback(
		(documentType: InboundDeliveryDocumentType, file: File) => {
			if (!isPdfFile(file)) {
				toast.error(`${INBOUND_DOCUMENT_LABELS[documentType]} must be a PDF.`);
				return;
			}

			if (file.size >= INBOUND_DELIVERY_DOCUMENT_MAX_BYTES) {
				toast.error(`${INBOUND_DOCUMENT_LABELS[documentType]} must be smaller than 1 MB.`);
				return;
			}

			setDocumentDraft(documentType, () => ({
				file,
				removeExisting: false,
			}));
		},
		[setDocumentDraft],
	);

	const handleDialogOpenChange = React.useCallback((open: boolean) => {
		if (!open) {
			setDialogState(null);
			setDocumentDrafts(createEmptyDocumentDrafts());
			setIsSaving(false);
		}
	}, []);

	const persistDocuments = React.useCallback(
		async (delivery: InboundDeliveryRecord) => {
			const errors: string[] = [];

			for (const documentType of Object.keys(INBOUND_DOCUMENT_LABELS) as InboundDeliveryDocumentType[]) {
				const draft = documentDrafts[documentType];
				const existingDocument = getDeliveryDocument(delivery, documentType);

				if (draft.file) {
					try {
						const buffer = await draft.file.arrayBuffer();
						await uploadInboundDeliveryDocument({
							deliveryId: delivery._id as any,
							documentType,
							fileData: buffer,
							contentType: draft.file.type || "application/pdf",
							filename: draft.file.name,
							fileSize: draft.file.size,
						});
					} catch (error) {
						errors.push(
							`${INBOUND_DOCUMENT_LABELS[documentType]}: ${error instanceof Error ? error.message : "Upload failed"}`,
						);
					}
					continue;
				}

				if (draft.removeExisting && existingDocument) {
					try {
						await removeInboundDeliveryDocument({
							deliveryId: delivery._id as any,
							documentType,
						});
					} catch (error) {
						errors.push(
							`${INBOUND_DOCUMENT_LABELS[documentType]}: ${error instanceof Error ? error.message : "Remove failed"}`,
						);
					}
				}
			}

			return errors;
		},
		[documentDrafts, removeInboundDeliveryDocument, uploadInboundDeliveryDocument],
	);

	const handleSubmit = React.useCallback(async () => {
		if (!dialogState || isSaving) return;

		const shipperName = form.shipperName.trim();
		if (!shipperName) {
			toast.error("Shipper name is required.");
			return;
		}

		const scheduledDeliveryAt = parseDateTimeInputValue(form.scheduledDeliveryAt);
		if (scheduledDeliveryAt.invalid || scheduledDeliveryAt.value === undefined) {
			toast.error("Enter a valid scheduled delivery date and time in Chicago time.");
			return;
		}

		const timeInInput = parseTimeInputValue(form.timeIn);
		if (timeInInput.invalid) {
			toast.error("Enter a valid time in.");
			return;
		}
		const timeIn = combineDatePartAndTimeValue(form.scheduledDeliveryAt, timeInInput.value);
		if (timeIn.invalid) {
			toast.error("Time in needs the scheduled delivery date and a valid Chicago time.");
			return;
		}

		const timeOutInput = parseTimeInputValue(form.timeOut);
		if (timeOutInput.invalid) {
			toast.error("Enter a valid time out.");
			return;
		}
		const timeOut = combineDatePartAndTimeValue(form.scheduledDeliveryAt, timeOutInput.value);
		if (timeOut.invalid) {
			toast.error("Time out needs the scheduled delivery date and a valid Chicago time.");
			return;
		}

		if (
			timeIn.value !== undefined &&
			timeOut.value !== undefined &&
			timeOut.value < timeIn.value
		) {
			toast.error("Time out cannot be earlier than time in.");
			return;
		}

		const payload = {
			shipperName,
			scheduledDeliveryAt: scheduledDeliveryAt.value,
			trackingReference: form.trackingReference.trim() || null,
			driverName: form.driverName.trim() || null,
			timeIn: timeIn.value ?? null,
			timeOut: timeOut.value ?? null,
			warehouseReportComplete: form.warehouseReportComplete,
			damage: form.damage,
			notes: form.notes.trim() || null,
			status: form.status,
		};

		setIsSaving(true);
		try {
			let savedDelivery: InboundDeliveryRecord;
			if (dialogState.mode === "edit") {
				savedDelivery = (await updateInboundDelivery({
					deliveryId: dialogState.delivery._id as any,
					...payload,
				})) as InboundDeliveryRecord;
			} else {
				savedDelivery = (await createInboundDelivery(payload)) as InboundDeliveryRecord;
			}

			const documentErrors = await persistDocuments(savedDelivery);
			if (documentErrors.length > 0) {
				toast.error(
					`Inbound delivery saved, but some documents failed: ${documentErrors.join("; ")}`,
				);
			} else {
				toast.success(
					dialogState.mode === "edit"
						? "Inbound delivery updated."
						: "Inbound delivery created.",
				);
			}

			setDialogState(null);
			setForm(buildEmptyFormState());
			setDocumentDrafts(createEmptyDocumentDrafts());
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to save inbound delivery.");
		} finally {
			setIsSaving(false);
		}
	}, [
		createInboundDelivery,
		dialogState,
		form,
		isSaving,
		persistDocuments,
		updateInboundDelivery,
	]);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Inbound Deliveries</h1>
					<p className="mt-1 text-muted-foreground">
						Manual warehouse inbound delivery notices for logistics, warehouse, and admin teams.
					</p>
				</div>
				<Button onClick={openCreateDialog}>Create Entry</Button>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Warehouse Inbounds</CardTitle>
					<CardDescription>
						All inbound deliveries are shown by default. Times are entered and displayed in Chicago time.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Tabs
						value={activeTab}
						onValueChange={(value) => setActiveTab(value as InboundDeliveriesTab)}
						className="mb-4"
					>
						<TabsList>
							<TabsTrigger value="all">
								All
								<Badge variant="secondary" className="ml-2">
									{tabCounts.all}
								</Badge>
							</TabsTrigger>
							<TabsTrigger value="scheduled">
								Scheduled
								<Badge variant="secondary" className="ml-2">
									{tabCounts.scheduled}
								</Badge>
							</TabsTrigger>
							<TabsTrigger value="received">
								Received
								<Badge variant="secondary" className="ml-2">
									{tabCounts.received}
								</Badge>
							</TabsTrigger>
						</TabsList>
					</Tabs>

					{inboundDeliveries === undefined ? (
						<DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
					) : (
						<InternalEntityDataTable
							data={filteredDeliveries}
							columns={columns}
							enableSelection={false}
						/>
					)}
				</CardContent>
			</Card>

			<Dialog open={dialogState !== null} onOpenChange={handleDialogOpenChange}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[720px]">
					<DialogHeader>
						<DialogTitle>
							{dialogState?.mode === "edit" ? "Edit Inbound Delivery" : "Create Inbound Delivery"}
						</DialogTitle>
						<DialogDescription>
							Use Chicago time for all delivery and receiving fields.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-5 py-4">
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="grid gap-2">
							<Label htmlFor="inbound-shipper-name">Shipper Name</Label>
							<Input
								id="inbound-shipper-name"
								value={form.shipperName}
								onChange={(event) => setField("shipperName", event.target.value)}
								placeholder="Carrier or shipper"
							/>
							</div>

							<div className="grid gap-2">
							<Label htmlFor="inbound-tracking-reference">Tracking / Ref #</Label>
							<Input
								id="inbound-tracking-reference"
								value={form.trackingReference}
								onChange={(event) => setField("trackingReference", event.target.value)}
								placeholder="Tracking, PRO, BOL, container, etc."
							/>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="inbound-scheduled-delivery">Scheduled Delivery</Label>
								<ChicagoDateTimePicker
									id="inbound-scheduled-delivery"
									value={form.scheduledDeliveryAt}
									onChange={(value) => setField("scheduledDeliveryAt", value)}
									placeholder="Pick the delivery date"
								/>
							</div>

							<div className="grid gap-2">
							<Label htmlFor="inbound-driver-name">Driver Name</Label>
							<Input
								id="inbound-driver-name"
								value={form.driverName}
								onChange={(event) => setField("driverName", event.target.value)}
								placeholder="Driver or receiving contact"
							/>
							</div>
						</div>

						<div className="rounded-xl border bg-muted/25 p-4">
							<div className="mb-4">
								<h3 className="text-sm font-semibold text-foreground">Receiving</h3>
								<p className="text-xs text-muted-foreground">
									Keep the scheduled delivery separate from the actual warehouse arrival times.
								</p>
							</div>
							<div className="grid gap-4 sm:grid-cols-3">
								<div className="grid gap-2">
									<Label htmlFor="inbound-time-in">Time In</Label>
									<ChicagoTimeInput
										id="inbound-time-in"
										value={form.timeIn}
										onChange={(value) => setField("timeIn", value)}
										disabled={!splitDateTimeValue(form.scheduledDeliveryAt).datePart}
									/>
								</div>

								<div className="grid gap-2">
									<Label htmlFor="inbound-time-out">Time Out</Label>
									<ChicagoTimeInput
										id="inbound-time-out"
										value={form.timeOut}
										onChange={(value) => setField("timeOut", value)}
										disabled={!splitDateTimeValue(form.scheduledDeliveryAt).datePart}
									/>
								</div>

								<div className="grid gap-2">
									<Label htmlFor="inbound-status">Inbound Status</Label>
									<Select value={form.status} onValueChange={(value) => setField("status", value as InboundDeliveryStatus)}>
										<SelectTrigger id="inbound-status">
											<SelectValue placeholder="Select status" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="scheduled">Scheduled</SelectItem>
											<SelectItem value="received">Received</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>

						<div className="rounded-xl border bg-muted/25 p-4">
							<div className="mb-4">
								<h3 className="text-sm font-semibold text-foreground">Warehouse Checklist</h3>
								<p className="text-xs text-muted-foreground">
									Track paperwork and condition separately from the inbound status.
								</p>
							</div>
							<div className="grid gap-3 sm:grid-cols-2">
								<div className="flex items-center justify-between rounded-lg border bg-background px-4 py-3">
									<div className="pr-4">
										<p className="text-sm font-medium text-foreground">Warehouse Report Complete</p>
										<p className="text-xs text-muted-foreground">
											Turn this on after the warehouse paperwork is done.
										</p>
									</div>
									<Switch
										id="inbound-warehouse-report"
										checked={form.warehouseReportComplete}
										onCheckedChange={(checked) => setField("warehouseReportComplete", checked)}
									/>
							</div>

								<div className="flex items-center justify-between rounded-lg border bg-background px-4 py-3">
									<div className="pr-4">
										<p className="text-sm font-medium text-foreground">Damage</p>
										<p className="text-xs text-muted-foreground">
											Use notes below for issue details or supporting context.
										</p>
									</div>
									<Switch
										id="inbound-damage"
										checked={form.damage}
										onCheckedChange={(checked) => setField("damage", checked)}
									/>
								</div>
							</div>
						</div>

						<div className="rounded-xl border bg-muted/25 p-4">
							<div className="mb-4">
								<h3 className="text-sm font-semibold text-foreground">Attachments</h3>
								<p className="text-xs text-muted-foreground">
									Optional DO, BOL, and Driver ID PDFs. Files upload to R2 when you save this inbound delivery.
								</p>
							</div>
							<div className="grid gap-3">
								<InboundDocumentUploadField
									documentType="do"
									existingDocument={
										dialogState?.mode === "edit"
											? getDeliveryDocument(dialogState.delivery, "do")
											: undefined
									}
									draft={documentDrafts.do}
									onSelectFile={(file) => handleDocumentFileSelection("do", file)}
									onClearSelectedFile={() =>
										setDocumentDraft("do", (current) => ({ ...current, file: null }))
									}
									onMarkRemoveExisting={() =>
										setDocumentDraft("do", () => ({ file: null, removeExisting: true }))
									}
									onUndoRemoveExisting={() =>
										setDocumentDraft("do", () => ({ file: null, removeExisting: false }))
									}
								/>
								<InboundDocumentUploadField
									documentType="bol"
									existingDocument={
										dialogState?.mode === "edit"
											? getDeliveryDocument(dialogState.delivery, "bol")
											: undefined
									}
									draft={documentDrafts.bol}
									onSelectFile={(file) => handleDocumentFileSelection("bol", file)}
									onClearSelectedFile={() =>
										setDocumentDraft("bol", (current) => ({ ...current, file: null }))
									}
									onMarkRemoveExisting={() =>
										setDocumentDraft("bol", () => ({ file: null, removeExisting: true }))
									}
									onUndoRemoveExisting={() =>
										setDocumentDraft("bol", () => ({ file: null, removeExisting: false }))
									}
								/>
								<InboundDocumentUploadField
									documentType="driverId"
									existingDocument={
										dialogState?.mode === "edit"
											? getDeliveryDocument(dialogState.delivery, "driverId")
											: undefined
									}
									draft={documentDrafts.driverId}
									onSelectFile={(file) => handleDocumentFileSelection("driverId", file)}
									onClearSelectedFile={() =>
										setDocumentDraft("driverId", (current) => ({ ...current, file: null }))
									}
									onMarkRemoveExisting={() =>
										setDocumentDraft("driverId", () => ({ file: null, removeExisting: true }))
									}
									onUndoRemoveExisting={() =>
										setDocumentDraft("driverId", () => ({ file: null, removeExisting: false }))
									}
								/>
							</div>
						</div>

						<div className="grid gap-2">
							<Label htmlFor="inbound-notes">Notes</Label>
							<Textarea
								id="inbound-notes"
								value={form.notes}
								onChange={(event) => setField("notes", event.target.value)}
								placeholder="Damage notes, receiving issues, arrival details, or follow-up context"
								rows={5}
							/>
						</div>

						{dialogState?.mode === "edit" ? (
							<div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
								<div>
									<span className="font-medium text-foreground">Created:</span>{" "}
									{formatChicagoDateTime(dialogState.delivery.createdAt)}
								</div>
								<div>
									<span className="font-medium text-foreground">Updated:</span>{" "}
									{formatChicagoDateTime(dialogState.delivery.updatedAt)}
								</div>
							</div>
						) : null}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={isSaving}>
							Cancel
						</Button>
						<Button onClick={() => void handleSubmit()} disabled={isSaving}>
							{isSaving ? (
								<>
									<Loader2 className="mr-2 size-4 animate-spin" />
									Saving
								</>
							) : dialogState?.mode === "edit" ? (
								"Save Changes"
							) : (
								"Create Entry"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
